import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Notification } from "@tql/ui-components-react/kendo/notification";
import { check, flag_01 } from "@tql/ui-components-react/untitled/icons";
import { useAtom } from "jotai";
import { useEffect, useMemo } from "react";

import type { RerateDispatchOutcome } from "../../state/rerate-dispatch-atoms.ts";
import { rerateDispatchOutcomeAtom } from "../../state/rerate-dispatch-atoms.ts";
import * as styles from "./rerate-dispatch-banner.module.css";

export const RERATE_DISPATCH_BANNER_TIMEOUT_MS = 20_000;
// A result the user never saw (e.g. they went back to the Quote Board) is
// dropped after this long instead of showing up later as fresh feedback.
export const RERATE_DISPATCH_MAX_UNSEEN_AGE_MS = 5 * 60_000;

const isStale = (outcome: RerateDispatchOutcome, now: number) =>
	outcome.expiresAt === undefined
		? now - outcome.tenderedAt > RERATE_DISPATCH_MAX_UNSEEN_AGE_MS
		: now >= outcome.expiresAt;

export const RERATE_DISPATCH_MESSAGES = {
	cancelSuccess: "Original dispatch canceled.",
	cancelFailure:
		"Original dispatch not canceled successfully. Please call the carrier to cancel the dispatch.",
	redispatchSuccess: "New dispatch successful.",
	redispatchFailure:
		"New dispatch not successful. Please call the carrier to handle the dispatch.",
} as const;

const DispatchResult = ({
	success,
	successMessage,
	failureMessage,
}: {
	readonly success: boolean;
	readonly successMessage: string;
	readonly failureMessage: string;
}) => (
	<Notification type={{ style: success ? "success" : "error", icon: false }}>
		<div className={styles.bannerContent}>
			<SvgIcon icon={success ? check : flag_01} size={"medium"} />
			<span>
				<strong>{success ? "Success:" : "Immediate Action:"}</strong>{" "}
				{success ? successMessage : failureMessage}
			</span>
		</div>
	</Notification>
);

// Shows the cancel/redispatch results from a rerate of a dispatched shipment
// for RERATE_DISPATCH_BANNER_TIMEOUT_MS in total, even across navigation.
// Render it keyed by PO so the mount time below is per shipment.
export const RerateDispatchBanner = ({
	poNumber,
}: {
	readonly poNumber: number;
}) => {
	const [outcome, setOutcome] = useAtom(rerateDispatchOutcomeAtom);
	// Judging staleness by mount time hides an expired result on return without
	// flashing it first.
	const mountedAt = useMemo(() => Date.now(), []);
	const visibleOutcome =
		outcome?.poNumber === poNumber && !isStale(outcome, mountedAt)
			? outcome
			: null;

	useEffect(() => {
		if (!visibleOutcome) {
			return;
		}

		const { tenderedAt } = visibleOutcome;
		const expiresAt =
			visibleOutcome.expiresAt ??
			Date.now() + RERATE_DISPATCH_BANNER_TIMEOUT_MS;
		if (visibleOutcome.expiresAt === undefined) {
			// Persist the expiry so leaving and returning can't restart the timeout.
			setOutcome((current) =>
				current === visibleOutcome ? { ...current, expiresAt } : current,
			);
		}

		// Clear only the result this timer was started for, never a newer one.
		const timeoutId = setTimeout(
			() => {
				setOutcome((current) =>
					current?.tenderedAt === tenderedAt ? null : current,
				);
			},
			Math.max(0, expiresAt - Date.now()),
		);

		return () => clearTimeout(timeoutId);
	}, [visibleOutcome, setOutcome]);

	if (!visibleOutcome) {
		return null;
	}

	const { cancelDispatchSuccess, redispatchSuccess } = visibleOutcome;

	return (
		<div
			aria-live={"polite"}
			className={styles.rerateDispatchBanner}
			data-testid={"rerate-dispatch-banner"}
			role={"status"}
		>
			{cancelDispatchSuccess === null ? null : (
				<DispatchResult
					failureMessage={RERATE_DISPATCH_MESSAGES.cancelFailure}
					success={cancelDispatchSuccess}
					successMessage={RERATE_DISPATCH_MESSAGES.cancelSuccess}
				/>
			)}
			{redispatchSuccess === null ? null : (
				<DispatchResult
					failureMessage={RERATE_DISPATCH_MESSAGES.redispatchFailure}
					success={redispatchSuccess}
					successMessage={RERATE_DISPATCH_MESSAGES.redispatchSuccess}
				/>
			)}
		</div>
	);
};
